export const LOOT_MODEL_MIN_ZOOM = 15.5;
export const LOOT_MODEL_MAX_VISIBLE = 32;

export const LOOT_MODEL_CONFIGS = Object.freeze({
  barrel: Object.freeze({
    key: 'barrel',
    label: 'Fass',
    categoryLabel: 'Mülleimer oder Müllcontainer',
    emoji: '🛢️',
    heightMeters: 8,
    altitudeOffset: 0.08,
    rotationOffset: 18,
    pivotOffset: [0, 0, 0],
    interactionRadius: 12,
  }),
  hammock: Object.freeze({
    key: 'hammock',
    label: 'Hängematte',
    categoryLabel: 'Parkbank',
    emoji: '🏝️',
    heightMeters: 7,
    altitudeOffset: 0.08,
    rotationOffset: -24,
    pivotOffset: [0, 0, 0],
    interactionRadius: 13,
  }),
  lighthouse: Object.freeze({
    key: 'lighthouse',
    label: 'Leuchtturm',
    categoryLabel: 'Markanter Ort oder Sehenswürdigkeit',
    emoji: '🗼',
    heightMeters: 18,
    altitudeOffset: 0.08,
    rotationOffset: 8,
    pivotOffset: [0, 0, 0],
    interactionRadius: 14,
  }),
  pirateShip: Object.freeze({
    key: 'pirateShip',
    label: 'Piratenschiff',
    categoryLabel: 'Bushaltestelle',
    emoji: '⛵',
    heightMeters: 9,
    altitudeOffset: 0.08,
    rotationOffset: 32,
    pivotOffset: [0, 0, 0],
    interactionRadius: 13,
  }),
  marketStall: Object.freeze({
    key: 'marketStall',
    label: 'Marktstand',
    categoryLabel: 'Supermarkt oder Pfandrückgabeautomat',
    emoji: '🏪',
    heightMeters: 9,
    altitudeOffset: 0.08,
    rotationOffset: -12,
    pivotOffset: [0, 0, 0],
    interactionRadius: 13,
  }),
});

const CATEGORY_MODEL_KEYS = Object.freeze({
  muelleimer: 'barrel',
  muelleimers: 'barrel',
  muellcontainer: 'barrel',
  muellcontainers: 'barrel',
  parkbank: 'hammock',
  parkbaenke: 'hammock',
  markanter_ort: 'lighthouse',
  markante_orte: 'lighthouse',
  sehenswuerdigkeit: 'lighthouse',
  sehenswuerdigkeiten: 'lighthouse',
  sehenswuedigkeit: 'lighthouse',
  sehenswuedigkeiten: 'lighthouse',
  bushaltestelle: 'pirateShip',
  bushaltestellen: 'pirateShip',
  supermarkt: 'marketStall',
  supermaerkte: 'marketStall',
  pfandrueckgabeautomat: 'marketStall',
  pfand_rueckgabeautomat: 'marketStall',
  pfandrueckgabeautomaten: 'marketStall',
  pfand_rueckgabeautomaten: 'marketStall',
});

export function normalizeLootCategory(value) {
  const raw = typeof value === 'object' && value
    ? value.key ?? value.slug ?? value.name ?? value.label ?? ''
    : value;
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function resolveLootModelKey(locationOrCategory) {
  const candidates = typeof locationOrCategory === 'object' && locationOrCategory
    ? [
      locationOrCategory.categoryKey,
      locationOrCategory.categoryLabel,
      locationOrCategory.category,
      ...(Array.isArray(locationOrCategory.categories) ? locationOrCategory.categories : []),
    ]
    : [locationOrCategory];
  for (const candidate of candidates) {
    const key = CATEGORY_MODEL_KEYS[normalizeLootCategory(candidate)];
    if (key) return key;
  }
  return null;
}

export function getLootModelConfig(locationOrCategory) {
  const key = resolveLootModelKey(locationOrCategory);
  return key ? LOOT_MODEL_CONFIGS[key] : null;
}

export function stableLocationRotation(location, offset = 0) {
  const value = String(location?.id ?? location?.location_id ?? `${location?.latitude}:${location?.longitude}`);
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) hash = ((hash * 31) + value.charCodeAt(index)) | 0;
  return offset + ((Math.abs(hash) % 21) - 10);
}

export function selectVisibleLootLocations(locations = [], view = {}) {
  const zoom = Number(view.zoom);
  if (!Number.isFinite(zoom) || zoom < LOOT_MODEL_MIN_ZOOM) return [];
  const bounds = view.bounds;
  const center = view.center || {};
  const inside = (location) => {
    const latitude = Number(location?.latitude ?? location?.lat);
    const longitude = Number(location?.longitude ?? location?.lng);
    if (![latitude, longitude].every(Number.isFinite) || !resolveLootModelKey(location)) return false;
    if (!bounds) return true;
    return latitude >= Number(bounds.south) && latitude <= Number(bounds.north)
      && longitude >= Number(bounds.west) && longitude <= Number(bounds.east);
  };
  const distanceSquared = (location) => {
    const latitude = Number(location?.latitude ?? location?.lat);
    const longitude = Number(location?.longitude ?? location?.lng);
    return ((latitude - Number(center.lat ?? center.latitude ?? latitude)) ** 2)
      + ((longitude - Number(center.lng ?? center.longitude ?? longitude)) ** 2);
  };
  return locations.filter(inside).sort((left, right) => distanceSquared(left) - distanceSquared(right)).slice(0, LOOT_MODEL_MAX_VISIBLE);
}

export function createModelLoadCache(loader, disposer = () => {}) {
  const entries = new Map();
  return {
    load(key, url) {
      if (entries.has(key)) return entries.get(key).promise;
      const entry = { state: 'loading', asset: null, error: null, promise: null };
      entry.promise = Promise.resolve()
        .then(() => loader(url, key))
        .then((asset) => {
          entry.state = 'ready';
          entry.asset = asset;
          return asset;
        })
        .catch((error) => {
          entry.state = 'error';
          entry.error = error;
          throw error;
        });
      entries.set(key, entry);
      return entry.promise;
    },
    get(key) {
      return entries.get(key) || null;
    },
    snapshot() {
      return Object.fromEntries([...entries].map(([key, entry]) => [key, entry.state]));
    },
    clear() {
      for (const entry of entries.values()) if (entry.asset) disposer(entry.asset);
      entries.clear();
    },
  };
}

export const LOOT_MODEL_LEGEND = Object.freeze(Object.values(LOOT_MODEL_CONFIGS));
